import React from 'react';
import PropTypes from 'prop-types';
import CIcon from '@coreui/icons-react';
import { CBadge, CLink } from '@coreui/react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { setFlexibleMultiModal } from '../../../../../../actions/common';
import { COMPONENT_NAME } from '../../../../../../constants';
import { TRIGGER_TYPE } from '../../../../../../constants/aiInsights';
import { capitalizeFirstLetter } from '../../../../../../utils';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const ViewReviewAnalyse = ({ insight, index, models, onRerun, isRerunning }) => {
    const runDate = insight?.createdAt ? dayjs.utc(insight.createdAt).format('MMM D, YYYY') : 'N/A';
    const completedDate = insight?.completedAt ? dayjs.utc(insight.completedAt).format('MMM D, YYYY') : null;
    const enableShowCost = localStorage.getItem('show-llm-costs') || window.location.search.includes('show-llm-costs=1');

    const dispatch = useDispatch();
    const intelligenceSetting = useSelector((state) => state.subscriber.intelligenceSetting);

    // Get status badge color
    const getStatusColor = () => {
        if (!insight?.status) return 'secondary';

        switch (insight.status) {
            case 'done':
                return 'success';
            case 'pending':
                return 'warning';
            case 'waiting':
                return 'info';
            case 'failed':
                return 'danger';
            default:
                return 'secondary';
        }
    };
    
    const recipientCount = insight?.emailDeliveryStatus === 'sent' ? insight?.emailedRecipients?.length || 0 : 0;

    const handleViewInsight = () => {
        dispatch(
            setFlexibleMultiModal({
                show: true,
                component: COMPONENT_NAME.AI_INSIGHTS_REVIEW_HISTORICAL_INSIGHTS_DETAIL,
                attributes: { insight },
            })
        );
    };

    const handleViewPrompt = () => {
        dispatch(
            setFlexibleMultiModal({
                show: true,
                component: COMPONENT_NAME.AI_INSIGHTS_REVIEW_HISTORICAL_INSIGHTS_PROMPT,
                attributes: { insight },
            })
        );
    };

    const modelsName = (models || []).find(model => model.id === insight.llmModelId)?.displayName || "";

    return (
         <div key={index} className="row-rule d-flex justify-content-between">
              <div className="info-wrap d-flex justify-content-between align-items-center py-2">
                   <div className="info-title">
                        <h4 style={{ display: 'flex', alignItems: 'center' }}>
                             <span className="mr-3">{runDate}</span>
                             <CBadge color={getStatusColor()}>{capitalizeFirstLetter(insight?.status) || 'Unknown'}</CBadge>
                        </h4>
                        <div className="d-flex align-items-center">
                             {completedDate && <span className="text-muted">Completed: {completedDate}</span>}
                        </div>
                   </div>
              </div>

              <div className="options-wrapper d-flex">
                   {enableShowCost && (
                        <div className="option-item hover-show-tooltip">
                             <CLink className="option text-primary">
                                  <CIcon height={14} icon="icon-info" className="icon-primary"/>
                             </CLink>

                             <div className="block-tooltip">
                                  <CIcon icon="arrowTooltip" className="arrow-tooltip-preview" />
                                  <div className="block-tooltip-text text-start font-size-12">
                                       <p>Model Used for analysis: {modelsName}</p>
                                       <p>Tokens In: {Number(insight.inputTokens).toLocaleString('en-US')}</p>
                                       <p>Tokens Out: {Number(insight.outputTokens).toLocaleString('en-US')}</p>
                                       <p>Total Tokens: {(Number(insight.inputTokens) + Number(insight.outputTokens)).toLocaleString('en-US')}</p>
                                       <p>Total cost for analysis: {Number(insight.totalCost).toLocaleString('en-US')}$</p>
                                  </div>
                             </div>
                        </div>
                   )}

                   {
                        insight.totalChargedCredits > 0 && intelligenceSetting?.aiBillingCycle && (
                         <div className="option-item hover-show-tooltip">
                              <CLink className="option text-primary">
                                   <CIcon icon="iconDollarSign" className="icon-primary"/>
                              </CLink>

                              <div className="block-tooltip">
                                   <CIcon icon="arrowTooltip" className="arrow-tooltip-preview" />
                                   <div className="block-tooltip-text text-start font-size-12">
                                        <p>AI Credits Used: {insight.totalChargedCredits}</p>
                                   </div>
                              </div>
                         </div>
                        )
                   }

                   <div className="option-item hover-show-tooltip save-report-bell notification-bell pl-0 mr-0">
                        <span className="option position-relative">
                             <CIcon icon="cilEnvelopeClosed" className={`text-primary`} />

                             {recipientCount > 0 && (
                                  <CBadge
                                       shape="pill"
                                       color="danger"
                                       className={`${recipientCount > 99 ? 'big-number' : ''} position-absolute right-5px`}
                                  >
                                       {recipientCount}
                                  </CBadge>
                             )}
                        </span>

                        <div className="block-tooltip">
                             <CIcon icon="arrowTooltip" className="arrow-tooltip-preview" />

                             <div className="block-tooltip-text font-size-12 d-flex flex-column gap-1">
                                  <p className='mb-0'>Email: {insight?.emailDeliveryStatus || 'Not sent'}</p>
                                  <p className='mb-0'>Recipients: {recipientCount}</p>
                                  {insight.isSaveResult && <p className='mb-0'>Saved from Preview</p>}
                             </div>
                        </div>
                   </div>

                   {onRerun && insight?.dateRangeJson && insight?.triggerType !== TRIGGER_TYPE.MANUAL_PREVIEW && (
                        <div className="option-item hover-show-tooltip">
                             <CLink
                                  className="option text-primary"
                                  onClick={() => onRerun(insight)}
                                  disabled={isRerunning || insight?.status === 'pending' || insight?.status === 'waiting'}
                                  style={{ cursor: isRerunning ? 'not-allowed' : 'pointer' }}
                             >
                                  <CIcon icon="iconReload" height={14} className={isRerunning ? 'spinner' : ''} />
                             </CLink>

                             <div className="block-tooltip">
                                  <CIcon icon="arrowTooltip" className="arrow-tooltip-preview" />
                                  <div className="block-tooltip-text font-size-12">
                                       <p>Re-run Insight</p>
                                  </div>
                             </div>
                        </div>
                   )}

                   <div className="option-item hover-show-tooltip">
                        <CLink className="option text-primary" onClick={handleViewInsight} disabled={insight?.status !== 'done'}>
                             <CIcon icon="icon-eye" height={14} />
                        </CLink>

                        <div className="block-tooltip">
                             <CIcon icon="arrowTooltip" className="arrow-tooltip-preview" />

                             <div className="block-tooltip-text font-size-12">
                                  <p>View Insight</p>
                             </div>
                        </div>
                   </div>
                   <div className="option-item hover-show-tooltip">
                        <CLink className="option text-primary" onClick={handleViewPrompt} disabled={insight?.status !== 'done'}>
                             <CIcon icon="icon-eye" height={14} />
                        </CLink>

                        <div className="block-tooltip">
                             <CIcon icon="arrowTooltip" className="arrow-tooltip-preview" />

                             <div className="block-tooltip-text font-size-12">
                                  <p>View Prompt</p>
                             </div>
                        </div>
                   </div>
              </div>
         </div>
    );
};

ViewReviewAnalyse.propTypes = {
     insight: PropTypes.shape({
          dateRangeJson: PropTypes.any,
          triggerType: PropTypes.any,
     }),
     index: PropTypes.any,
     models: PropTypes.any,
     onRerun: PropTypes.func,
     isRerunning: PropTypes.bool,
};

export default ViewReviewAnalyse;
